import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { MinimumDurationLoader } from "@/components/public/MinimumDurationLoader";
import { waitLoaderMinDelay } from "@/lib/lazyWithMinimumDelay";

const REJECTED_KEY = "adminAuthRejected";
const TEAM_LOADER_MESSAGE = "Preparing team access";

async function checkAdminList(email: string): Promise<boolean> {
  const t = email.trim();
  if (!t) return false;
  const { data, error } = await supabase
    .from("admin_users")
    .select("id")
    .ilike("email", t)
    .maybeSingle();
  if (error) return false;
  return !!data;
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [, goTo] = useLocation();

  useEffect(() => {
    if (sessionStorage.getItem(REJECTED_KEY)) {
      setError(true);
      sessionStorage.removeItem(REJECTED_KEY);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    void (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (session?.user?.email && (await checkAdminList(session.user.email))) {
        const forceChange = session.user.user_metadata?.force_password_change === true;
        await waitLoaderMinDelay(startedAt);
        if (cancelled) return;
        goTo(forceChange ? "/admin/change-password" : "/admin/overview");
        return;
      }
      setSessionReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [goTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    const { error: signInError, data: signInData } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (signInError || !signInData.user?.email) {
      setError(true);
      setLoading(false);
      return;
    }
    const allowed = await checkAdminList(signInData.user.email);
    if (!allowed) {
      await supabase.auth.signOut();
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(false);
    const forceChange = signInData.user.user_metadata?.force_password_change === true;
    goTo(forceChange ? "/admin/change-password" : "/admin/overview");
  };

  return (
    <MinimumDurationLoader ready={sessionReady} loaderProps={{ message: TEAM_LOADER_MESSAGE }}>
      <div className="min-h-screen bg-[#F5EBD8] flex">
      <div className="hidden lg:flex flex-col justify-between w-[420px] bg-[#1B2A4A] p-12 shrink-0">
        <div>
          <div className="w-10 h-10 rounded-xl bg-[#D4A853]/20 flex items-center justify-center mb-10">
            <span className="text-[#D4A853] font-serif font-bold text-lg">G</span>
          </div>
          <div className="h-px w-10 bg-[#D4A853]/40 mb-8" />
          <h2 className="text-white font-serif text-3xl font-bold leading-tight mb-4">
            Grand &amp; Kansas<br />Admin Portal
          </h2>
          <p className="text-white/40 text-sm leading-relaxed max-w-xs">
            Community engagement intelligence for the Grand &amp; Kansas North development in El Segundo&apos;s Smoky Hollow
            district.
          </p>
        </div>

        <div className="space-y-6">
          {[
            { n: "AI·1", label: "Community Chatbot", desc: "Admin demo preview" },
            { n: "AI·2", label: "Sentiment Analysis", desc: "76% positive this week" },
            { n: "AI·3", label: "Intelligence Report", desc: "Auto-generated weekly" },
          ].map((s) => (
            <div key={s.n} className="flex items-center gap-3">
              <span className="text-[10px] font-bold bg-[#D4A853]/15 text-[#D4A853] px-2 py-1 rounded font-mono shrink-0">
                {s.n}
              </span>
              <div>
                <p className="text-white/80 text-xs font-semibold">{s.label}</p>
                <p className="text-white/30 text-[11px]">{s.desc}</p>
              </div>
            </div>
          ))}

          <div className="pt-4 border-t border-white/8">
            <p className="text-white/20 text-xs">Greystar × Griffin Capital</p>
            <p className="text-white/15 text-[10px] mt-0.5">El Segundo, CA · Confidential</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#1B2A4A] mb-5">
              <span className="text-[#D4A853] font-serif font-bold text-lg">G</span>
            </div>
            <h1 className="font-serif text-2xl font-bold text-[#1B2A4A] mb-1">Grand &amp; Kansas</h1>
            <p className="text-xs text-[#1B2A4A]/40 tracking-widest uppercase">Admin Portal</p>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1B2A4A] mb-1">Welcome back</h2>
            <p className="text-sm text-slate-400">Sign in to your team account to continue.</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-7">
            <div className="h-0.5 w-8 bg-[#D4A853] rounded-full mb-6" />

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="admin-email" className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                  Email Address
                </label>
                <input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 text-[#1B2A4A] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]/30 focus:border-[#D4A853]/60 transition-all bg-slate-50/50 placeholder:text-slate-300"
                  placeholder="jude@greystar.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                  Password
                </label>
                <input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 text-[#1B2A4A] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]/30 focus:border-[#D4A853]/60 transition-all bg-slate-50/50 placeholder:text-slate-300"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && (
                <p role="alert" className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                  Invalid email or password
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#1B2A4A] text-white text-sm font-semibold rounded-lg hover:bg-[#243960] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 mt-1"
              >
                {loading ? (
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="text-center mt-6">
            <Link href="/" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
              ← Return to public site
            </Link>
          </div>

          <p className="text-center text-[10px] text-slate-300 mt-4 uppercase tracking-widest">
            Confidential · Team Access Only
          </p>
        </div>
      </div>
    </div>
    </MinimumDurationLoader>
  );
}
